<script setup lang="ts">
  import { computed, ref } from 'vue';

  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';

  defineOptions({ name: 'll-obfuscate-text' });

  import obfuscateText, { ObfuscateTextOptions } from '../../utils/obfuscateText';

  export interface ObfuscateTextProps extends ObfuscateTextOptions {
    /**
     * The full text
     */
    text: string;

    /**
     * Whether to obfuscate all characters.
     * @deprecated omit the lengthToKeep prop to achieve the same result
     */
    obfuscateAll?: boolean;

    /**
     * Displays a button for toggling text visibility
     */
    showToggle?: boolean;
  }

  const isFullTextVisible = ref(false);

  const props = withDefaults(defineProps<ObfuscateTextProps>(), {
    maskChar: '•',
    maskCharCount: 10,
    lengthToKeep: 0,
    position: 'start',
    obfuscateAll: false,
    showToggle: false,
  });

  const obfuscatedText = computed(() => {
    if (props.showToggle && isFullTextVisible.value) {
      return props.text;
    }

    if (props.obfuscateAll) {
      return props.maskChar.repeat(props.maskCharCount);
    }

    return obfuscateText(props.text, {
      maskChar: props.maskChar,
      maskCharCount: props.maskCharCount,
      lengthToKeep: props.lengthToKeep,
      position: props.position,
    });
  });
</script>

<template>
  <span class="stash-obfuscate-text" data-test="stash-obfuscate-text">
    {{ obfuscatedText }}
    <Button
      v-if="props.showToggle"
      class="ml-1 size-auto p-0"
      icon
      type="button"
      @click="isFullTextVisible = !isFullTextVisible"
    >
      <Icon
        :name="isFullTextVisible ? 'hide' : 'show'"
        class="cursor-pointer"
        :class="isFullTextVisible ? 'stash-obfuscate-text__hide-toggle-icon' : 'stash-obfuscate-text__show-toggle-icon'"
        :data-test="
          isFullTextVisible ? 'stash-obfuscate-text|hide-toggle-icon' : 'stash-obfuscate-text|show-toggle-icon'
        "
      />
    </Button>
  </span>
</template>
